import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MediaRef } from '@astrally/shared';
import {
  MEDIA_IMAGE_CONTENT_TYPES,
  MEDIA_MAX_BYTES,
  MediaError,
  preparePostImage,
} from '../../lib/media';
import { useApp } from '../../state/AppState';
import { useI18n } from '../../i18n';
import { moderationRefusal, mediaProRequired, type PreparedUpload } from '../../lib/backend/types';

/**
 * The composer.
 *
 * TWO RULES SHAPE THIS WHOLE FILE.
 *
 * 1. THE PRO REQUIREMENT IS STATED BEFORE A FILE IS PICKED, NEVER AFTER.
 *    Finding out about a paywall after you have chosen the photo, waited for
 *    it to resize and watched a progress bar is the most annoying possible
 *    order to learn it in — the work is already spent and the refusal reads as
 *    a bait. So a non-Pro account is never shown a file picker at all: it is
 *    shown what the picker would be for, that it belongs to Pro, and a way to
 *    read about Pro. There is no `<input type="file">` in the non-Pro branch,
 *    so there is nothing to click by accident.
 *
 * 2. THE PRIMARY CTA IS NEVER DISABLED. "Post" stays pressable with an empty
 *    box; pressing it says what is missing. A greyed-out button is a puzzle —
 *    the reader has to guess which of several requirements they have failed —
 *    and it is the exact failure SocialOptInScreen exists to remember.
 *
 * NO VIDEO HERE — photos only. Nothing can check a clip: the moderation
 * provider reads text and images, and sampling one frame would be claiming
 * coverage we do not have. Video stays a chat attachment, where it reaches one
 * person who already accepted a connection request rather than every member of
 * the feed. The caption below says so where someone looks for the button.
 *
 * COPY IS HARDCODED ENGLISH HERE, not `t()` keys. app/src/i18n/en.ts is typed,
 * so every key must exist there or it is a compile error — and that file was
 * outside this change's file scope. Keys to add when it is next opened, at
 * which point every literal below becomes a `t()` call: feed.title,
 * feed.placeholder, feed.post, feed.needText, feed.remaining, feed.proMedia,
 * feed.proMediaBody, feed.addPhoto,
 * feed.empty, feed.emptyBody, feed.loadMore, feed.demoNoMedia. The existing
 * moderation strings (`msg.moderation.blocked` / `.unavailable`) are already
 * reused below rather than re-written.
 */

/** Matches FEED_TEXT_MAX in functions/src/feed/feedFns.ts. Server re-checks it. */
const TEXT_MAX = 500;

/** Show the counter only when it starts to matter. */
const COUNTER_FROM = TEXT_MAX - 100;

type Attachment = { prepared: PreparedUpload; previewUrl: string | null };

export function PostComposer({ onPosted }: { onPosted(): void }) {
  const { t } = useI18n();
  const app = useApp();
  const [text, setText] = useState('');
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'info' | 'danger'; text: string } | null>(null);
  const imageInput = useRef<HTMLInputElement>(null);

  const isPro = app.entitlement?.tier === 'pro';
  const isDemo = app.backend.kind === 'demo';
  const remaining = TEXT_MAX - text.length;

  const clearAttachment = () => {
    setAttachment((current) => {
      if (current?.previewUrl) URL.revokeObjectURL(current.previewUrl);
      return null;
    });
  };

  /**
   * Resize / probe on the DEVICE before anything is uploaded.
   *
   * Every byte uploaded is a byte served back, repeatedly, forever — so the
   * 1280px / 300KB image target is hit here with a canvas rather than by a
   * server-side transcoding function, which would be a compute bill and an
   * operational surface to do work the phone does for free.
   */
  const pick = async (file: File) => {
    setNotice(null);
    setBusy(true);
    try {
        const image = await preparePostImage(file);
      clearAttachment();
      setAttachment({
        prepared: {
          kind: 'image',
          blob: image.blob,
          contentType: image.contentType,
          width: image.width,
          height: image.height,
          blurhash: image.blurhash,
          bytes: image.bytes,
        },
        previewUrl: URL.createObjectURL(image.blob),
      });
    } catch (e) {
      // MediaError messages are written to be shown verbatim: they name the
      // limit and what to do about it. Anything else gets a plain fallback.
      setNotice({
        tone: 'danger',
        text: e instanceof MediaError ? e.message : t('common.error'),
      });
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    const body = text.trim();
    if (!body) {
      setNotice({ tone: 'info', text: 'Write something first — even one line is a post.' });
      return;
    }
    if (body.length > TEXT_MAX) {
      setNotice({ tone: 'info', text: `That’s ${body.length - TEXT_MAX} characters over the limit.` });
      return;
    }

    setBusy(true);
    setNotice(null);
    try {
      let media: MediaRef | undefined;
      if (attachment) media = await app.backend.uploadPostMedia(attachment.prepared);
      await app.backend.createPost(body, media);
      // Only now is anything published. Clearing before the call would lose
      // the draft on a refusal — which is exactly when the author most wants it.
      setText('');
      clearAttachment();
      onPosted();
    } catch (e) {
      const refusal = moderationRefusal(e);
      if (refusal === 'blocked') {
        setNotice({ tone: 'danger', text: t('msg.moderation.blocked') });
      } else if (refusal === 'unavailable') {
        setNotice({ tone: 'info', text: t('msg.moderation.unavailable') });
      } else if (mediaProRequired(e)) {
        setNotice({
          tone: 'info',
          text: 'Sharing photos is part of Astrally Pro. Your words are still here — post them, or open Pro from your profile.',
        });
      } else {
        setNotice({
          tone: 'danger',
          text: String(e).replace(/^(FirebaseError|Error):\s*/g, '').slice(0, 200),
        });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card card--strong" style={{ marginBottom: 14 }}>
      <textarea
        className="input"
        rows={3}
        value={text}
        maxLength={TEXT_MAX}
        placeholder="Say something to the people here…"
        aria-label="Write a post"
        onChange={(e) => setText(e.target.value)}
        style={{ resize: 'vertical' }}
      />

      {/* --- attachment ------------------------------------------------------
          Stated BEFORE a file is chosen, in both branches. */}
      {isPro ? (
        <div className="col" style={{ gap: 6, marginTop: 10 }}>
          <div className="row wrap">
            <input
              ref={imageInput}
              type="file"
              accept={MEDIA_IMAGE_CONTENT_TYPES.join(',')}
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Reset first: picking the same file twice in a row fires no
                // change event otherwise, and a retry after an error looks dead.
                e.target.value = '';
                if (file) void pick(file);
              }}
            />
            <button className="chip" disabled={busy} onClick={() => imageInput.current?.click()}>
              Add a photo
            </button>
          </div>
          <div className="tiny faint" style={{ lineHeight: 1.5 }}>
            {/* An image check exists now (checkImage on the moderation
                provider, verified live 2026-08-09) — but it FAILS OPEN, so
                "screened" is the honest verb and "guaranteed" is not. This
                string spent a while claiming a check that did not exist; keep
                the hedge, and keep the video sentence, which claims nothing. */}
            Photos are resized on your phone to about {Math.round(MEDIA_MAX_BYTES / 1000)}KB and
            screened automatically before they appear — screening can be skipped if the checker is
            unreachable, and it never catches everything. Anything posted here reaches every member,
            so post accordingly and report what doesn’t belong. Video is a chat attachment only:
            nothing can check a clip, and unreviewed footage reaching every member is a different
            proposition from sending it to someone who has accepted your request.
          </div>
          {isDemo && (
            <div className="tiny faint">
              Demo mode keeps everything on this device and has nowhere to store a file, so an
              attachment won’t post here.
            </div>
          )}
        </div>
      ) : (
        // NO FILE PICKER AT ALL for a non-Pro account. Nothing to tap, nothing
        // to resize, nothing to be refused after the fact.
        <div
          className="row"
          style={{
            marginTop: 10,
            padding: 10,
            borderRadius: 'var(--radius-sm)',
            border: '1px dashed var(--border-strong)',
            alignItems: 'flex-start',
          }}
        >
          <span aria-hidden style={{ fontSize: '1rem' }}>
            🔒
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="tiny" style={{ lineHeight: 1.5 }}>
              Photos are part of Astrally Pro — posting them and seeing them both. Text
              posts are included for every member, and yours reach everyone the same way.
            </div>
            <Link
              to="/profile/subscription"
              className="tiny"
              style={{ display: 'inline-block', marginTop: 6, color: 'var(--accent-ink)' }}
            >
              {t('pro.upgrade')} →
            </Link>
          </div>
        </div>
      )}

      {attachment && (
        <div className="row" style={{ marginTop: 10 }}>
          {attachment.previewUrl && (
            <img
              src={attachment.previewUrl}
              alt=""
              style={{
                width: 52,
                height: 52,
                objectFit: 'cover',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
              }}
            />
          )}
          <div className="tiny muted" style={{ flex: 1, minWidth: 0 }}>
            {`Photo · ${attachment.prepared.width}×${attachment.prepared.height} · ${Math.round(attachment.prepared.bytes / 1000)}KB`}
          </div>
          <button className="chip" disabled={busy} onClick={clearAttachment}>
            Remove
          </button>
        </div>
      )}

      {notice && (
        <div
          className={`banner banner--${notice.tone === 'danger' ? 'danger' : 'info'} small spread`}
          style={{ marginTop: 10 }}
        >
          <span>{notice.text}</span>
          <button className="chip" aria-label={t('common.close')} onClick={() => setNotice(null)}>
            ✕
          </button>
        </div>
      )}

      <div className="spread" style={{ marginTop: 10 }}>
        <span className="tiny faint">
          {text.length >= COUNTER_FROM ? `${remaining} left` : ''}
        </span>
        {/* Never disabled for an unmet requirement — only while a post is
            genuinely in flight, where a second press would publish twice. */}
        <button className="btn btn--primary btn--small" disabled={busy} onClick={submit}>
          {busy ? <span className="spinner" /> : 'Post'}
        </button>
      </div>
    </div>
  );
}
